import { addCreatedMetadata, useApp, useAuth, usePopUpMessage } from '@hb/react'
import { Reaction, ReactionType, ThreadType } from '@hb/shared'
import { arrayUnion, DocumentReference, updateDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { toggleReactionFunction } from '../../backend/functions/other'

/**
 * Hook for adding reactions to non-thread documents (e.g., comments).
 * Uses direct Firestore updates.
 */
export const useAddReaction = (docRef: DocumentReference, propPath: string) => {
  const { processResponse } = usePopUpMessage()
  const { appName } = useApp()
  const [addingReaction, setAddingReaction] = useState(false)
  const addReaction = useCallback(
    async (type: Reaction['type']) => {
      setAddingReaction(true)
      try {
        await updateDoc(docRef, propPath, arrayUnion(addCreatedMetadata({ type }, appName)))
      } catch (err: any) {
        processResponse({
          error: err?.message || 'An error occurred adding the reaction',
        })
      }
      setAddingReaction(false)
    },
    [docRef, propPath, processResponse, appName],
  )

  return { addReaction, addingReaction }
}

/**
 * Hook for toggling reactions on thread messages.
 * Uses a Firebase function with proper authorization.
 */
export const useToggleReaction = (
  threadType: ThreadType,
  threadId: string,
  messageGroupId: string,
  messageId: string,
) => {
  const { processResponse } = usePopUpMessage()
  const [togglingReaction, setTogglingReaction] = useState<ReactionType | null>(null)
  const toggleReaction = useCallback(
    async (reactionType: ReactionType) => {
      setTogglingReaction(reactionType)
      try {
        const result = await toggleReactionFunction({
          threadId,
          type: threadType,
          messageGroupId,
          messageId,
          reactionType,
        })
        return result.data
      } catch (err: any) {
        processResponse({
          error: err?.message || 'An error occurred toggling the reaction',
        })
      } finally {
        setTogglingReaction(null)
      }
    },
    [threadId, threadType, messageGroupId, messageId, processResponse],
  )

  return { toggleReaction, togglingReaction }
}

export const useMyReactions = (reactions: Reaction[] | undefined) => {
  const { authUser } = useAuth()
  const uid = useMemo(() => authUser?.uid, [authUser])
  return useMemo(
    () => reactions?.filter(reaction => reaction.createdBy === uid) ?? [],
    [reactions, uid],
  )
}
